import { boundingBoxesType } from '../../types/aiOcrResult';

/**
 * result_data の既定値キーを省いて保存サイズを縮める（#3311）。
 *
 * Firestore は1ドキュメント1MBが上限で、明細が数百行ある帳票では
 * result_data がこれを超えて保存自体が失敗する。
 * 実データ（238行×61列＝14518セル）では、値が無いセルも
 * `{"value":null,"bounding_boxes":[],"confidence":1,"page":null,"is_uncertain":false}`
 * と1セル82バイトを消費しており、これが全体の過半を占めていた。
 *
 * ここでは「既定値と同じ値のキー」だけを保存対象から外す。
 * セル自体は必ず残すため、項目（列）が消えて表示やCSVの列がずれることはない。
 *
 * 読み出し側は「キーが無い＝既定値」として扱えるよう、いずれも
 * `'bounding_boxes' in value` / `confidence !== undefined` / `is_uncertain !== true`
 * のようにキーの有無に耐える形で読んでいるため、補完は必須ではない。
 * セルを既定値込みの形で受け取りたい箇所では expandResultDataCell を使う。
 *
 * page / label_page は省略しない。
 * 値が無いセルの page は null だが、これは「ページ不明」という意味を持つ。
 * 省略して undefined にしても画面側（ImageOverlay の isCurrentPageData）は
 * null / undefined を同じ「ページ不明」として扱うため表示は変わらないものの、
 * page はサイズ削減への寄与が小さい（実測 45.4% → 43.3%）一方で
 * 取り違えたときの影響が大きいため、そのまま保存する。
 */

/** 省略されたキーを補完した後のセル。読み出し側はこの形を前提にできる */
export interface ExpandedCell {
  value: unknown;
  bounding_boxes: boundingBoxesType[];
  confidence: number;
  is_uncertain: boolean;
  [key: string]: unknown;
}

/** 既定値。この値と一致するキーは保存しない */
const CELL_DEFAULTS = {
  bounding_boxes: [] as boundingBoxesType[],
  label_bounding_boxes: [] as boundingBoxesType[],
  confidence: 1,
  is_uncertain: false,
  is_fixed_value: false,
  is_calculated: false,
  calculation_error: '',
} as const;

/**
 * セル（スカラーの value を持つオブジェクト）かどうか。
 *
 * 実データのセルの value は常にスカラー（文字列 / 数値 / 真偽値 / null）で、
 * オブジェクトや配列にはならない（GetStringType / GetNumberType / GetBooleanType を参照）。
 * value がオブジェクト・配列のノードはセルではない（かたまりや明細）ため、
 * ここでセル扱いにしてしまうと中の既定値キーを誤って落とす恐れがある。
 * そのままセルでないノードとして再帰の対象にする。
 */
const isCell = (node: unknown): node is Record<string, unknown> => {
  if (node === null || typeof node !== 'object' || Array.isArray(node)) return false;
  const obj = node as Record<string, unknown>;
  if (!('value' in obj)) return false;
  const value = obj.value;
  return value === null || typeof value !== 'object';
};

/** キーの値が既定値と同じか。空配列は中身が空かどうかで判定する */
const isDefaultValue = (key: string, value: unknown): boolean => {
  if (!(key in CELL_DEFAULTS)) return false;
  const defaultValue = CELL_DEFAULTS[key as keyof typeof CELL_DEFAULTS];

  if (Array.isArray(defaultValue)) {
    return Array.isArray(value) && value.length === 0;
  }
  return value === defaultValue;
};

/** セル1件から既定値のキーを取り除く */
const compactCell = (cell: Record<string, unknown>): Record<string, unknown> => {
  const compacted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(cell)) {
    if (isDefaultValue(key, value)) continue;
    compacted[key] = value;
  }
  return compacted;
};

/**
 * result_data 全体を走査して、各セルの既定値キーを省く。
 * 元のオブジェクトは変更しない。
 */
export const compactResultData = <T>(resultData: T): T => {
  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (isCell(node)) return compactCell(node);
    if (node !== null && typeof node === 'object') {
      const next: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
        next[key] = walk(value);
      }
      return next;
    }
    return node;
  };

  return walk(resultData) as T;
};

/**
 * 省略されたキーに既定値を補ってセルを復元する（#3311）。
 *
 * 省略前に保存された既存レコードは既定値キーを持っているため、
 * その場合は保存済みの値をそのまま使う（上書きしない）。
 */
export const expandResultDataCell = (cell: Record<string, unknown>): ExpandedCell =>
  ({
    bounding_boxes: CELL_DEFAULTS.bounding_boxes,
    confidence: CELL_DEFAULTS.confidence,
    is_uncertain: CELL_DEFAULTS.is_uncertain,
    ...cell,
  }) as ExpandedCell;

/** セルの bounding_boxes を安全に取り出す。省略されていれば空配列を返す */
export const getBoundingBoxes = (cell: unknown): boundingBoxesType[] => {
  if (!isCell(cell)) return [];
  const boxes = cell.bounding_boxes;
  return Array.isArray(boxes) ? (boxes as boundingBoxesType[]) : [];
};
